const DOSSIER_STOCK_LABELS = {
  10: "10 — Welda",
  15: "15",
  20: "20",
  70: "70 — AWTech",
};

/**
 * Usable quantity: physical IV minus reserved R, never below 0.
 * Top-level MMS `Voorraad` stays physical; shop/site display uses this.
 */
const getAvailableStock = (iv, reserved = 0) => {
  const physical = Number(iv);
  const res = Number(reserved);
  const stock = Number.isFinite(physical) ? physical : 0;
  const reservedQty = Number.isFinite(res) ? res : 0;
  return Math.max(0, stock - reservedQty);
};

/**
 * Shop/site display qty from article fields (no location breakdown).
 * Returns null when Voorraad is missing.
 */
const availableVoorraadFromProduct = (product) => {
  if (product?.Voorraad === undefined || product?.Voorraad === null) {
    return null;
  }
  return getAvailableStock(product.Voorraad, product?.gereserveerd);
};

/**
 * Traffic-light stock color from quantity vs minimum stock (MV).
 * red = 0, orange = below MV but > 0, green = at/above MV (or any stock if MV is 0/missing).
 */
const getVoorraadKleur = (voorraad, minVoorraad) => {
  if (voorraad === undefined || voorraad === null) return "unknown";
  const stock = Number(voorraad);
  if (!Number.isFinite(stock) || stock === 0) return "secondary";
  const mv = Number(minVoorraad);
  if (!Number.isFinite(mv) || mv <= 0) return "success";
  if (stock < mv) return "warning";
  return "success";
};

const locationHasPositiveStock = (row) => {
  const qty = Number(row?.IV);
  if (Number.isFinite(qty)) return qty > 0;
  return row?.voorraadKleur === "success" || row?.voorraadKleur === "warning";
};

/**
 * Best traffic-light across warehouses: any green wins, else any orange, else red.
 */
const overallVoorraadKleur = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return "unknown";
  if (rows.some((row) => row?.voorraadKleur === "success")) return "success";
  if (rows.some((row) => row?.voorraadKleur === "warning")) return "warning";
  if (rows.some((row) => Number(row?.IV) > 0)) return "success";
  return "secondary";
};

/**
 * True when the current-dossier location is empty but another warehouse has stock.
 * Used to show a half-filled stock indicator instead of a full green/orange dot.
 */
const hasOnlyRemoteStock = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return false;
  const direct = rows.find((row) => row?.isDirect);
  if (!direct || locationHasPositiveStock(direct)) return false;
  return rows.some((row) => !row?.isDirect && locationHasPositiveStock(row));
};

const networkVoorraadFromLocations = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  let total = 0;
  let hasAny = false;
  for (const row of rows) {
    const n = Number(row?.IV);
    if (!Number.isFinite(n)) continue;
    total += n;
    hasAny = true;
  }
  return hasAny ? total : null;
};

const locatiesFromVoorraadDetail = (detail) => {
  if (!detail || typeof detail !== "object" || Array.isArray(detail)) {
    return [];
  }

  const rows = [];
  for (const [magKey, node] of Object.entries(detail)) {
    if (!node || typeof node !== "object" || Array.isArray(node)) {
      continue;
    }
    const magNr = Number(node.magNr);
    if (magNr !== 1) {
      continue;
    }
    const lok = String(node.address?.lok ?? "").trim();
    if (!lok) {
      continue;
    }
    const plaatsen =
      node.ligplaatsen != null &&
      typeof node.ligplaatsen === "object" &&
      !Array.isArray(node.ligplaatsen)
        ? node.ligplaatsen
        : {};
    let iv = 0;
    let reserved = 0;
    let minVoorraad = 0;
    for (const cell of Object.values(plaatsen)) {
      iv += Number(cell?.IV ?? 0);
      reserved += Number(cell?.R ?? 0);
      const mv = Number(cell?.minVoorraad ?? 0);
      if (Number.isFinite(mv) && mv > minVoorraad) {
        minVoorraad = mv;
      }
    }
    // Shop/site show usable stock (IV − R), not physical IV.
    const available = getAvailableStock(iv, reserved);
    rows.push({
      magKey,
      magNr: node.magNr ?? null,
      lok,
      IV: available,
      minVoorraad,
      voorraadKleur: getVoorraadKleur(available, minVoorraad),
    });
  }
  return rows;
};

const getDossierStockLabel = (dossierNr) => {
  const n = Number(dossierNr);
  return DOSSIER_STOCK_LABELS[n] || String(dossierNr);
};

const publicVoorraadPerDossier = (entries) => {
  if (!Array.isArray(entries)) return [];
  return entries.map((entry) => ({
    dossierNr: entry.dossierNr,
    voorraadKleur: entry.voorraadKleur,
    isHuidigDossier: entry.isHuidigDossier,
    locaties: locatiesFromVoorraadDetail(entry.VoorraadDetail).map(
      ({ lok, voorraadKleur, IV }) => ({ lok, voorraadKleur, IV })
    ),
  }));
};

const KLEUR_RANK = {
  danger: 0,
  secondary: 0,
  warning: 1,
  success: 2,
  unknown: 3,
};

const aggregateLocatiesByLok = (locaties) => {
  const byLok = new Map();
  for (const loc of locaties ?? []) {
    const lok = String(loc?.lok ?? "").trim();
    if (!lok) continue;
    const ivRaw = loc.IV;
    const hasIv =
      ivRaw !== null && ivRaw !== undefined && Number.isFinite(Number(ivRaw));
    const iv = hasIv ? Number(ivRaw) : undefined;
    const minVoorraad = Number(loc.minVoorraad) || 0;
    const prev = byLok.get(lok);
    if (!prev) {
      byLok.set(lok, {
        lok,
        IV: iv,
        minVoorraad,
        voorraadKleur: loc.voorraadKleur,
        hasIv,
      });
      continue;
    }
    if (hasIv && prev.hasIv) {
      prev.IV += iv;
      prev.minVoorraad = Math.max(prev.minVoorraad, minVoorraad);
    } else if (hasIv && !prev.hasIv) {
      prev.IV = iv;
      prev.hasIv = true;
      prev.minVoorraad = minVoorraad;
    }
    const prevRank = KLEUR_RANK[prev.voorraadKleur] ?? 3;
    const nextRank = KLEUR_RANK[loc.voorraadKleur] ?? 3;
    if (nextRank < prevRank) {
      prev.voorraadKleur = loc.voorraadKleur;
    }
  }
  return [...byLok.values()]
    .map(({ hasIv, ...row }) => ({
      lok: row.lok,
      IV: hasIv ? row.IV : undefined,
      voorraadKleur: hasIv
        ? getVoorraadKleur(row.IV, row.minVoorraad)
        : row.voorraadKleur,
    }))
    .sort((a, b) => a.lok.localeCompare(b.lok, "nl"));
};

const locatiesFromDossierEntry = (entry) => {
  if (entry?.VoorraadDetail) {
    return locatiesFromVoorraadDetail(entry.VoorraadDetail);
  }
  if (!Array.isArray(entry?.locaties)) {
    return [];
  }
  return entry.locaties;
};

/**
 * Location rows for the product-details stock list, grouped by address.lok.
 * Uses MMS VoorraadDetail / VoorraadPerDossier, not CAC.
 */
const stockLocationsFromProduct = (product) => {
  if (!product) return [];
  const locaties = [];
  const seenDossiers = new Set();
  let directLok = null;
  for (const entry of product.VoorraadPerDossier ?? []) {
    if (entry?.dossierNr != null) {
      seenDossiers.add(Number(entry.dossierNr));
    }
    const entryLocaties = locatiesFromDossierEntry(entry);
    if (entry?.isHuidigDossier && !directLok) {
      directLok = entryLocaties[0]?.lok || null;
    }
    locaties.push(...entryLocaties);
  }
  const currentDossier = Number(product.dossiernr ?? product.dossierNr);
  const currentAlreadyIncluded =
    Number.isFinite(currentDossier) && seenDossiers.has(currentDossier);
  if (product.VoorraadDetail && !currentAlreadyIncluded) {
    const currentLocaties = locatiesFromVoorraadDetail(product.VoorraadDetail);
    if (!directLok) {
      directLok = currentLocaties[0]?.lok || null;
    }
    locaties.push(...currentLocaties);
  } else if (product.VoorraadDetail && !directLok) {
    directLok = locatiesFromVoorraadDetail(product.VoorraadDetail)[0]?.lok || null;
  }
  return aggregateLocatiesByLok(locaties)
    .map((row) => ({
      ...row,
      isDirect: Boolean(directLok) && row.lok === directLok,
    }))
    .sort((a, b) => {
      if (a.isDirect !== b.isDirect) {
        return a.isDirect ? -1 : 1;
      }
      return a.lok.localeCompare(b.lok, "nl");
    });
};

module.exports = {
  getAvailableStock,
  availableVoorraadFromProduct,
  getVoorraadKleur,
  overallVoorraadKleur,
  hasOnlyRemoteStock,
  networkVoorraadFromLocations,
  getDossierStockLabel,
  locatiesFromVoorraadDetail,
  publicVoorraadPerDossier,
  stockLocationsFromProduct,
};
